import Table from './ResponsiveTable';
import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Typography,
} from "@mui/material";

// Summary of persisted blockers only. Live availability is checked when opening resources.
export function proposalProgress(proposal: any) {
  const items = proposal.items || [];
  const machines = items.filter(
    (item: any) => item.requires_manual_review && !item.review_resolved
  ).length;
  const selected = items.filter((item: any) => item.selected).length;
  const resources = ["driver", "truck", "warehouse"].filter(
    (key) => !proposal[key + "_id"] || !proposal.resources?.[key]?.name
  ).length;
  const availability =
    proposal.daily_policy?.mode !== "daily" && proposal.load_assessment &&
    (!proposal.availability_review ||
      Date.now() - Date.parse(proposal.availability_review.recorded_at) >=
        86400000)
      ? 1
      : 0;
  const groups = [
    Number(machines > 0) +
      Number(!selected) +
      (proposal.priority_problems || []).length,
    0, // Schedule estimates are optional and never block approval.
    Number(resources > 0) +
      (proposal.load_assessment?.problems || []).length +
      availability,
  ];
  const blocked = groups.reduce((sum, n) => sum + n, 0);
  const sent = proposal.status === "submitted";
  const ready =
    proposal.status === "ready_for_review" && !blocked && selected > 0;
  return {
    selected,
    blocked,
    sent,
    ready,
    step:
      groups.findIndex((n) => n > 0) < 0 ? 3 : groups.findIndex((n) => n > 0),
    warnings: [...(proposal.route_optimization?.schedule?.problems || []), ...(proposal.route_optimization?.schedule?.warnings || ["Sin configurar"])].length,
  };
}
export default function RouteOverview({
  proposals,
  onSelect,
  statusLabel,
  busy,
  generating,
}: any) {
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const rows = proposals.map((proposal: any) => ({
    proposal,
    ...proposalProgress(proposal),
  }));
  const visible = rows
    .filter(
      (row: any) =>
        (filter === "all" ||
          (filter === "blocked" && row.blocked > 0 && !row.sent) ||
          (filter === "ready" && row.ready) ||
          (filter === "sent" && row.sent)) &&
        [
          row.proposal.route_name,
          row.proposal.resources?.driver?.name,
          row.proposal.resources?.truck?.name,
        ]
          .join(" ")
          .toLocaleLowerCase("es")
          .includes(search.toLocaleLowerCase("es"))
    )
    .sort((a: any, b: any) =>
      a.proposal.route_name.localeCompare(b.proposal.route_name, "es")
    );
  const currentPage = Math.min(
    page,
    Math.max(0, Math.ceil(visible.length / 8) - 1)
  );
  return (
    <>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "repeat(2,1fr)", md: "repeat(4,1fr)" },
          gap: 2,
          my: 2.5,
        }}
      >
        {[
          ["Rutas en la generación", rows.length],
          [
            "Con bloqueos",
            rows.filter((row: any) => row.blocked && !row.sent).length,
          ],
          ["Listas para enviar", rows.filter((row: any) => row.ready).length],
          [
            "Máquinas seleccionadas",
            rows.reduce((sum: number, row: any) => sum + row.selected, 0),
          ],
        ].map(([label, value], index) => (
          <Paper variant="outlined" key={label} sx={{ p: 2.5 }}>
            <Typography variant="body2" color="text.secondary">
              {label}
            </Typography>
            <Typography
              variant="h4"
              fontWeight={700}
              sx={{ mt: 1 }}
              color={
                index === 1
                  ? "error.main"
                  : index === 2
                  ? "success.main"
                  : "text.primary"
              }
            >
              {value}
            </Typography>
          </Paper>
        ))}
      </Box>
      <Paper variant="outlined" sx={{ overflow: "hidden" }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          gap={2}
          sx={{ p: 2 }}
        >
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {[
              ["all", `Todas · ${rows.length}`],
              ["blocked", "Con bloqueos"],
              ["ready", "Listas para enviar"],
              ["sent", "Enviadas"],
            ].map(([id, label]) => (
              <Button
                key={id}
                variant={filter === id ? "contained" : "outlined"}
                sx={
                  filter === id
                    ? { bgcolor: "#192531" }
                    : { borderColor: "#dce3ef", color: "text.secondary" }
                }
                onClick={() => {
                  setFilter(id);
                  setPage(0);
                }}
              >
                {label}
              </Button>
            ))}
          </Stack>
          <TextField
            label="Buscar ruta, conductor o vehículo"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 0, width: { xs: "100%", sm: 280 } }}
          />
        </Stack>
        <Box sx={{ overflowX: "auto" }}>
          <Table
            aria-label="Propuestas de rutas"
            sx={{
              "& th": {
                bgcolor: "#f8fafc",
                color: "text.secondary",
                fontSize: 12,
              },
              "& td": { py: 2 },
            }}
          >
            <TableHead>
              <TableRow>
                {[
                  "Ruta",
                  "Estado",
                  "Revisión",
                  "Conductor",
                  "Vehículo",
                  "Máquinas",
                  "Pendientes",
                  "",
                ].map((label, i) => (
                  <TableCell key={i}>{label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {visible
                .slice(currentPage * 8, currentPage * 8 + 8)
                .map((row: any) => (
                  <TableRow key={row.proposal._id} hover>
                    <TableCell>
                      <Button
                        onClick={() => onSelect(row.proposal)}
                        disabled={busy}
                        sx={{ p: 0, color: "text.primary", fontWeight: 700 }}
                      >
                        {row.proposal.route_name}
                      </Button>
                      <Typography
                        variant="caption"
                        display="block"
                        color="text.secondary"
                      >
                        {row.proposal.resources?.warehouse?.name ||
                          "Sin confirmar"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        variant="outlined"
                        label={statusLabel(
                          row.proposal.status,
                          row.proposal.items
                        )}
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: 190 }}>
                      {row.proposal.daily_policy?.mode !== "daily" && <Stack direction="row" gap={0.5} sx={{ mb: 1 }}>
                        {[0, 1, 2, 3].map((step) => (
                          <Box
                            key={step}
                            sx={{
                              height: 5,
                              flex: 1,
                              borderRadius: 1,
                              bgcolor: row.sent
                                ? "#36865a"
                                : step === row.step
                                ? "#91b8e7"
                                : "#e1e6ef",
                            }}
                          />
                        ))}
                      </Stack>}
                      <Typography variant="caption">
                        {row.sent
                          ? "Servicio enviado"
                          : row.proposal.daily_policy?.mode === "daily" ? (!row.selected ? "Sin servicio necesario" : row.blocked ? "Revisar excepciones" : "Lista para revisión conjunta") : `Paso ${row.step + 1} de 4 · ${
                              [
                                "Revisar máquinas",
                                "Preparar recorrido",
                                "Confirmar recursos",
                                "Revisar y enviar",
                              ][row.step]
                            }`}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {row.proposal.resources?.driver?.name || "Sin confirmar"}
                    </TableCell>
                    <TableCell>
                      {row.proposal.resources?.truck?.name || "Sin confirmar"}
                    </TableCell>
                    <TableCell align="right">{row.selected}</TableCell>
                    <TableCell>
                      <Stack gap={0.5}>
                        {!row.sent && row.blocked > 0 && (
                          <Chip
                            label={`${row.blocked} bloqueos`}
                            sx={{ bgcolor: "#ffeded", color: "#b3261e" }}
                          />
                        )}
                        {row.warnings > 0 && (
                          <Chip
                            label={`${row.warnings} ${
                              row.warnings === 1 ? "aviso" : "avisos"
                            }`}
                            sx={{ bgcolor: "#fff4e1", color: "#925800" }}
                          />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        disabled={busy}
                        aria-label={`Abrir ${row.proposal.route_name}`}
                        onClick={() => onSelect(row.proposal)}
                      >
                        Abrir ›
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              {!visible.length && (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    sx={{ height: 240, textAlign: "center" }}
                  >
                    {generating
                      ? "Generando propuestas… La lista se actualizará al terminar."
                      : "No existen propuestas para esta fecha o búsqueda."}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
        <TablePagination
          component="div"
          count={visible.length}
          page={currentPage}
          rowsPerPage={8}
          rowsPerPageOptions={[8]}
          onPageChange={(_, next) => setPage(next)}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}–${to} de ${count} rutas · ordenadas por nombre`
          }
          getItemAriaLabel={(type) =>
            type === "next" ? "Página siguiente" : "Página anterior"
          }
        />
      </Paper>
    </>
  );
}
